<script lang="ts" setup>
import { BsError, BsSelect, BsCheckbox } from '@afo/bsf-core'
import type { ISelectOption } from '@afo/bsf-core/types'
import { msProducts, EUIFormVariant, EUiFormColor } from '@/domains/direct-sales/const/form.const'
import { COMPANY_SIZES } from '@/domains/direct-sales/const/companySizes.const'
import type { IProps, IUiFormProducts } from '@/domains/direct-sales/types/form.types'
import { useDirectSalesForm } from '@/domains/direct-sales/composables/useDirectSalesForm'
import Recaptcha from '@/domains/recaptcha/components/Recaptcha/Recaptcha.vue'
import DirectSalesRodo from '@/domains/direct-sales/components/direct-sales-rodo/direct-sales-rodo.vue'
import TextInput from '@/components/Inputs/TextInput/TextInput.vue'
import { useRouteLink } from '@/composables/useRouteLink'
import { ROUTE_PATHS } from '@/utils/route'

const { findRouteName } = useRouteLink()
const fallbackRedirectTo = findRouteName(ROUTE_PATHS.CONTACT_ASK_ABOUT_OFFER_FOR_EMPLOYEES_THANKS)

const props = withDefaults(defineProps<IProps>(), {
  color: EUiFormColor.WHITE,
  image: '/images/image_contact.jpg',
  header: '',
  badge: '',
  formId: '',
  redirectTo: '',
  variant: EUIFormVariant.Products,
})

const showList = ref(false)

const {
  config,
  $v,
  form,
  productForm,
  consentForm,
  consents,
  setAllConsents,
  isDisabled,
  isProducts,
  allConsentsChecked,
  countryOptions,
  recaptchaValid,
  submitForm,
  expand,
} = useDirectSalesForm(props, {
  fallbackRedirectTo,
})

const showSelectList = () => {
  showList.value = !showList.value
}

const hideSelectList = () => {
  showList.value = false
}

const toggleProduct = (value: string) => {
  const idx = (productForm as IUiFormProducts).products.indexOf(value)
  if (idx >= 0) {
    (productForm as IUiFormProducts).products.splice(idx, 1)
  } else {
    (productForm as IUiFormProducts).products.push(value)
  }
  $v.value.products?.$touch()
}

const selectedProducts = computed(() => (productForm as IUiFormProducts).products.join(', '))
</script>

<template>
  <UiSection
    id="formularz"
    :color="color"
    @click.self="hideSelectList"
  >
    <div
      class="form"
      @click.self="hideSelectList"
    >
      <div class="form__content">
        <h2
          v-if="header"
          class="form__header"
          v-html="header"
        />
        <div class="form__image">
          <span
            v-if="badge"
            class="form__image_badge"
            :style="{ backgroundImage: `url(${badge})` }"
          />
          <img
            :src="image"
            alt="Skontaktuj się z nami"
          >
        </div>
      </div>

      <div class="form__form">
        <form
          :action="config.formApiLink"
          method="POST"
          @click.self="hideSelectList"
          @submit.prevent="submitForm"
        >
          <div class="form__form_row">
            <div class="form__half">
              <TextInput
                id="firstName"
                v-model="form.firstName"
                name="firstName"
                :label="$t('form.name')"
                :error-message="$v.firstName.$errors[0]?.$message"
                :error="!!$v.firstName.$errors[0]?.$message"
                :required="true"
                @blur="$v.firstName.$touch()"
              />
            </div>

            <div class="form__half">
              <TextInput
                id="lastName"
                v-model="form.lastName"
                name="lastName"
                :label="$t('form.surname')"
                :error-message="$v.lastName.$errors[0]?.$message"
                :error="!!$v.lastName.$errors[0]?.$message"
                :required="true"
                @blur="$v.lastName.$touch()"
              />
            </div>
          </div>

          <div class="form__full">
            <TextInput
              id="email"
              v-model="form.email"
              name="email"
              :label="$t('form.email')"
              :error-message="$v.email.$errors[0]?.$message"
              :error="!!$v.email.$errors[0]?.$message"
              :required="true"
              @blur="$v.email.$touch()"
            />
          </div>

          <div class="form__form_row">
            <div class="form__half">
              <BsError :message="$v.country.$errors[0]?.$message">
                <BsSelect
                  id="country"
                  v-model="form.country as ISelectOption"
                  :label="$t('form.country.name')"
                  :placeholder="$t('form.country.choose')"
                  :options="countryOptions"
                  :error="!!$v.country.$errors[0]?.$message"
                  @update:model-value="$v.country.$touch()"
                />
              </BsError>
            </div>

            <div class="form__half">
              <TextInput
                id="phone"
                v-model="form.phone"
                name="phone"
                :label="$t('form.phone')"
                :error-message="$v.phone.$errors[0]?.$message"
                :error="!!$v.phone.$errors[0]?.$message"
                :required="true"
                @blur="$v.phone.$touch()"
              />
            </div>
          </div>

          <div class="form__full">
            <TextInput
              id="companyName"
              v-model="form.companyName"
              name="companyName"
              :label="$t('form.company')"
              :error-message="$v.companyName.$errors[0]?.$message"
              :error="!!$v.companyName.$errors[0]?.$message"
              :required="true"
              @blur="$v.companyName.$touch()"
            />
          </div>

          <div class="form__full">
            <TextInput
              id="nip"
              v-model="form.nip"
              name="nip"
              :label="$t('form.nip')"
              :error-message="$v.nip.$errors[0]?.$message"
              :error="!!$v.nip.$errors[0]?.$message"
              :required="true"
              @blur="$v.nip.$touch()"
            />
          </div>

          <div
            v-if="isProducts"
            class="form__full"
          >
            <BsError :message="$v.products?.$errors[0]?.$message">
              <label
                for="products"
                :class="{ 'label--error': !!$v.products?.$errors[0]?.$message }"
              >
                {{ $t('form.products.name') }}
              </label>
              <div class="form__form_select--multiple">
                <div
                  :class="[
                    'form__form_input',
                    'form__form_select',
                    'multiple',
                    { error: !!$v.products?.$errors[0]?.$message },
                    { 'form__form_select--expand': showList },
                  ]"
                >
                  <div @click="showSelectList">
                    <label v-if="selectedProducts">
                      {{ selectedProducts }}
                    </label>
                    <label v-else>
                      {{ $t('form.country.choose') }}
                    </label>
                  </div>

                  <transition name="select-fade">
                    <div
                      v-show="showList"
                      class="form_form_selectList"
                    >
                      <label
                        v-for="product in msProducts"
                        :key="product.label"
                        :for="product.value"
                      >
                        <input
                          :id="product.value"
                          type="checkbox"
                          :value="product.value"
                          :checked="productForm.products.includes(product.value)"
                          @change="toggleProduct(product.value)"
                        >
                        {{ $t(product.label) }}
                      </label>
                    </div>
                  </transition>
                </div>
              </div>
            </BsError>
          </div>

          <div class="form__full">
            <BsError :message="$v.companySize.$errors[0]?.$message">
              <BsSelect
                id="companySize"
                v-model="form.companySize as ISelectOption"
                :label="$t('form.companySize.name')"
                :placeholder="$t('form.country.choose')"
                :options="COMPANY_SIZES"
                :error="!!$v.companySize.$errors[0]?.$message"
                @update:model-value="$v.companySize.$touch()"
              />
            </BsError>
          </div>

          <div class="form__full">
            <BsError :message="$v.description.$errors[0]?.$message">
              <label
                for="description"
                :class="{ 'label--error': !!$v.description.$errors[0]?.$message }"
              >
                {{ $t('form.messege') }}
              </label>
              <div
                :class="[
                  'form__form_input form__form_input--textarea',
                  { error: !!$v.description.$errors[0]?.$message },
                ]"
              >
                <textarea
                  id="description"
                  v-model="form.description"
                  name="description"
                  @blur="$v.description.$touch()"
                />
              </div>
            </BsError>
          </div>

          <div class="consents">
            <div class="form__form_checkbox">
              <BsCheckbox
                id="consent-all"
                name="consentAll"
                :model-value="allConsentsChecked"
                :value="allConsentsChecked"
                :label="$t('consent.all')"
                @update:model-value="setAllConsents"
              />
            </div>

            <span
              class="consents__more"
              :class="{'consents__more--expand' : expand}"
              @click="expand = !expand"
            >
              {{ !expand ? $t('consent.more.show') : $t('consent.more.hide') }}
            </span>

            <transition name="expand">
              <div
                v-show="expand"
                class="consents__options"
              >
                <div
                  v-for="el in consents"
                  :key="el.key"
                  class="form__form_checkbox"
                >
                  <BsCheckbox
                    :id="`consent-${el.key}`"
                    v-model="consentForm[el.key].value"
                    :name="el.key"
                    :value="consentForm[el.key].value"
                    :label="$t(el.text)"
                  />
                </div>
              </div>
            </transition>

            <div
              class="form__form_checkbox"
              :class="{ error: !!$v.terms?.$errors[0]?.$message }"
            >
              <BsCheckbox
                id="consent-terms"
                v-model="form.terms"
                name="consentTerms"
                :value="form.terms"
                :label="$t('consent.terms')"
                :error="!!$v.terms?.$errors[0]?.$message"
                @update:model-value="$v.terms?.$touch()"
              />
            </div>
          </div>

            <div class="form__form_captcha">
              <Recaptcha>
                <input
                  class="form__form_button"
                  type="submit"
                  :value="$t('form.submit')"
                  :disabled="isDisabled || recaptchaValid"
                >
              </Recaptcha>
            </div>
        </form>
        
        <DirectSalesRodo />
      </div>
    </div>
  </UiSection>
</template>

<style lang="scss" scoped>
.section {
  margin: 0.8rem;
  padding: 4.8rem 1.6rem;
}

.form {
  $root: &;

  display: flex;
  flex-direction: column;

  &__header {
    font-size: 2.8rem;
    line-height: 3.6rem;

    :deep(small) {
      margin-top: 0.8rem;
      font-size: 1.8rem;
      line-height: 2.4rem;
    }

    :deep(span) {
      color: var(--span-color);
    }
  }

  &__image {
    position: relative;
    width: 100%;
    height: 24rem;
    margin-top: 4rem;
    margin-bottom: 3.2rem;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
      border-radius: 1.6rem;
    }

    &_badge {
      position: absolute;
      top: -4.8rem;
      right: -1.6rem;
      z-index: 2;
      display: inline-block;
      width: 9.6rem;
      height: 9.6rem;
      background-repeat: no-repeat;
      background-position: center;
      background-size: cover;
    }
  }

  &__form {

    :deep(.bs-input__input),
    :deep(.bs-select__toggler) {
      background-color: $white;
    }

    :deep(.bs-input__label),
    :deep(.bs-select__label) {
      font-weight: 400;
    }

    :deep(.bs-error) {
      margin-bottom: 0.8rem;
    }

    .form__full {
      clear: both;
      width: 100%;
      margin-top: 1.6rem;

      :deep(.bs-error),
      :deep(.bs-input),
      :deep(.bs-select) {
        width: 100%;
      }
    }

    &_row {
      display: flex;
      flex-direction: column;
      gap: 1.6rem;
      margin-top: 1.6rem;

      &:first-of-type {
        margin-top: 0;
      }

      .form__half {
        float: none;
        width: 100%;
        margin: 0;

        label,
        :deep(.bs-input__label),
        :deep(.bs-select__label) {
          white-space: nowrap;
        }

        :deep(.bs-error),
        :deep(.bs-input),
        :deep(.bs-select) {
          width: 100%;
        }
      }
    }

    .form__half {
      #{$root}__form_input {
        margin-bottom: 0;
      }

      &>span.error {
        display: inline-block;
        margin-top: 0.8rem;
        color: red;
      }
    }

    label {
      display: inline-block;
      margin-bottom: 0.4rem;
      margin-left: 0.4rem;
      color: $text-300;
      font-weight: 400;
      font-size: 1.4rem;
      line-height: 2rem;

      &.label--error {
        color: $invalid-500;
      }
    }

    .consents {
      margin-top: 3.2rem;
      overflow-wrap: anywhere;

      &__more {
        @include body-2-medium;
        display: flex;
        margin-bottom: 1.6rem;
        padding: 0 3.3rem 0;
        transition: transform 0.3s ease-in-out;

        &:after {
          display: inline-block;
          width: 2.4rem;
          height: 2.4rem;
          margin-left: 0.8rem;
          background-image: url('/icons/icon_arrow_down.svg');
          background-repeat: no-repeat;
          background-position: center;
          background-size: cover;
          content: '';
        }

        &--expand {
          &:after {
            transform: rotate(180deg);
          }
        }
      }

      &__options {
        display: flex;
        flex-direction: column;
        gap: 1.2rem;
        padding: 0 3.3rem 1.6rem;

        .form__form_checkbox label {
          @include body-2;
        }
      }

      :deep(.bs-checkbox__label) {
        @include body-2;
        color: $text-300;

        .label__link {
          color: $text-400;
          text-decoration: underline;
        }

        .underline {
          text-decoration: underline;
        }
      }
    }

    &_checkbox {
      position: relative;
      display: flex;

      label {
        @include body-2-medium;

        :deep(.underline) {
          text-decoration: underline;
        }

        :deep(.label__link) {
          color: $text-400;
          text-decoration: underline;
        }
      }

      input {
        min-width: 2.4rem;
        width: 2.4rem;
        height: 2.4rem;
        margin-right: 0.8rem;
        padding: 0.4rem;
        border: 0.1rem solid $text-400;
        border-radius: 0.4rem;

        &:checked {
          background-image: url('/icons/icon_check.svg');
          background-repeat: no-repeat;
          background-position: center;
        }
      }

      &.error {
        input {
          border: 0.1rem solid red;
        }

        span.error {
          position: absolute;
          bottom: -1rem;
          color: red;
          font-size: 1rem;
        }
      }
    }

    &_input {
      position: relative;
      width: 100%;
      max-height: 4rem;
      height: 4rem;
      margin-bottom: 1.6rem;
      background-color: $white;
      border: 0.1rem solid $gray-400;
      border-radius: 1.6rem;
      transition: border-color 0.2s;

      &:hover {
        border-color: $text-400;
      }

      &-phone {
        display: flex;

        input:not(:last-of-type) {
          width: 4.5rem;
          padding-right: 0;
        }

        input:nth-of-type(2) {
          padding-left: 0;
        }
      }

      &.error {
        border: 0.1rem solid red;
      }

      &>.error {
        display: inline-block;
        margin-top: 0.8rem;
        color: red;
      }

      &--textarea {
        max-height: 9.6rem;
        height: 9.6rem;
        margin-bottom: 0;
      }

      input,
      select,
      textarea {
        width: 100%;
        height: 100%;
        padding: 0.8rem 1.6rem;
        font-weight: 400;
        font-size: 1.6rem;
        line-height: 2.4rem;
        background-color: transparent;

        &::-webkit-input-placeholder {
          color: $text-200;
        }

        &:-ms-input-placeholder {
          color: $text-200;
        }

        &::placeholder {
          color: $text-200;
        }
      }

      select {
        -moz-appearance: none;
        -webkit-appearance: none;
        appearance: none;
      }
    }

    .form_form_selectList {
      position: absolute;
      top: calc(100% + 0.4rem);
      right: 0;
      left: 0;
      z-index: 2;
      padding: 1.6rem;
      background-color: $white;
      border: 0.1rem solid $gray-400;
      border-radius: 1.6rem;
    }

    .select-fade-enter-active,
    .select-fade-leave-active {
      transition: opacity 0.5s ease;
    }

    .select-fade-enter-from,
    .select-fade-leave-to {
      opacity: 0;
    }

    &_select {

      &--multiple {
        margin-bottom: 0;
      }

      &.multiple {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        margin-bottom: 0;
        padding: 0.8rem 1.6rem 1.6rem;

        label {
          position: relative;
          display: flex;
          align-items: center;
          margin: 0;
          color: $text-400;
          font-weight: 500;
          font-size: 1.6rem;
          line-height: 2.4rem;
          cursor: pointer;

          &:not(:last-of-type) {
            margin-bottom: 1.6rem;
          }

          input {
            min-width: 2.4rem;
            width: 2.4rem;
            height: 2.4rem;
            margin-right: 0.8rem;
            padding: 0;
            border: 0.1rem solid $text-400;
            border-radius: 0.4rem;

            &:checked {
              background-image: url('/icons/icon_check.svg');
              background-repeat: no-repeat;
              background-position: center;
            }
          }
        }
      }

      &:after {
        position: absolute;
        top: 1rem;
        right: 1rem;
        display: inline-block;
        width: 2.4rem;
        height: 2.4rem;
        margin-left: 0.8rem;
        background-image: url('/icons/icon_arrow_down.svg');
        background-repeat: no-repeat;
        background-position: center;
        background-size: cover;
        transform: rotate(0);
        transition: transform 0.2s;
        pointer-events: none;
        content: '';
      }

      &--expand {
        &:after {
          transform: rotate(-180deg)
        }
      }
    }

    &_button {
      display: inline-block;
      min-width: 8.6rem;
      width: auto;
      padding: 1.4rem 2.4rem;
      color: $white;
      font-weight: 500;
      font-size: 1.4rem;
      line-height: 2rem;
      text-align: center;
      background-color: $text-400;
      border-radius: 5rem;
      cursor: pointer;

      &:disabled {
        background-color: $gray-400;
        cursor: not-allowed;
      }
    }
  }
}

.expand-enter-active,
.expand-leave-active {
  overflow: hidden;
  transition: max-height 0.3s ease-in-out, opacity 0.2s ease-in-out;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.expand-enter-to,
.expand-leave-from {
  max-height: 100rem;
  opacity: 1;
}

@include atXS {
  .form__image {
    height: 54rem;
  }

  .form__form_row {
    flex-direction: row;
    align-items: flex-start;
  }

  .form__form_row .form__half {
    flex: 1 1 0;
    width: auto;
  }
}

@include atS {
  .form {
    flex-direction: row;
    gap: 6.4rem;
    max-width: 115.2rem;
    margin: 0 auto;
  }

  .form__content,
  .form__form {
    min-width: 0;
    width: 50%;
  }

  .form__image_badge {
    right: -4.8rem;
  }
}

@include atM {
  .section {
    margin: 2.4rem;
    padding: 12rem 2.4rem;
  }

  .form {
    gap: 13rem;
  }

  .form__header {
    font-size: 4rem;
    line-height: 4.8rem;

    :deep(small) {
      font-size: 2.8rem;
      line-height: 3.6rem;
    }
  }

  .form__image {
    margin-top: 10.7rem;
    margin-bottom: 0;
  }

  .form__image_badge {
    top: -7.3rem;
    right: -7.3rem;
    width: 14.7rem;
    height: 14.7rem;
  }

  .form__form_input {
    max-height: 4.8rem;
    height: 4.8rem;
  }

  .form__form_input--textarea {
    max-height: 10.2rem;
    height: 10.2rem;
  }

  .form__form_input input,
  .form__form_input select,
  .form__form_input textarea {
    padding: 1.2rem 1.6rem;
  }

  .form__form_select.multiple {
    padding: 1.2rem 1.6rem 1.6rem;
  }

  .form__form_captcha {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }
}

@include atXL {
  .form {
    max-width: 134rem;
  }
}
</style>
